/**
 * The connected wallet's on-chain balances: a wallet never sees a mock number.
 * No address in the descriptor or a failed read is `null`, shown as "—",
 * never a mock and never "0.00".
 */

import { useCallback, useEffect, useState } from "react";
import { readBalance } from "./erc20";
import { tokenAddress } from "./tokens";

export interface TokenBalances {
  /** symbol → base units, or null when unreadable. Missing while loading. */
  values: Readonly<Record<string, bigint | null>>;
  /** True until the first read for the current owner/symbols settles. */
  loading: boolean;
  /** Re-reads every symbol (used after a submit and by the refresh button). */
  refresh: () => void;
}

/**
 * `owner` null (demo, or signed out): no wallet call and no values, so callers
 * show their demo view.
 */
export function useTokenBalances(
  symbols: readonly string[],
  owner: string | null,
): TokenBalances {
  const [values, setValues] = useState<Record<string, bigint | null>>({});
  const [loading, setLoading] = useState(owner !== null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  // Symbols arrive as a fresh array on every render; key the effect on the
  // content instead so re-renders don't re-read the chain in a loop.
  const symbolKey = symbols.join(",");

  useEffect(() => {
    if (owner === null) {
      setValues({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const list = symbolKey === "" ? [] : symbolKey.split(",");

    void Promise.all(
      list.map(async (symbol) => {
        const address = tokenAddress(symbol);
        if (address === null) return [symbol, null] as const;
        return [symbol, await readBalance(address, owner)] as const;
      }),
    ).then((entries) => {
      if (cancelled) return;
      setValues(Object.fromEntries(entries));
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [owner, symbolKey, nonce]);

  return { values, loading, refresh };
}
